import { AspectRatio, Box, SimpleGrid } from "@chakra-ui/react";
import { DecorativeBackgroundImage } from "components/images";
import { type CaseStudyIndustry, type CaseStudySimple } from "lib/sanity";
import { sortBy, uniqBy } from "lodash";
import Image from "next/image";
import React, { useMemo } from "react";
import { Card } from "ui/cards";
import { type FilterTabProps, FilterTabs } from "ui/tabs";
import { getCaseStudyIndustryColor } from "utils";
import cardOverlay from "../../../public/assets/cards/green-media-overlay.png";

type IndustryTabProps = {
  tabOrder: number;
} & FilterTabProps;

const industryTabOrder: Array<CaseStudyIndustry["slug"]> = ["retail", "media", "fintech", "b2b"];

export function CaseStudiesCatalog({ caseStudies }: { caseStudies: CaseStudySimple[] }) {
  const allCaseStudies = useMemo(() => {
    return <CaseStudyGrid caseStudies={caseStudies} />;
  }, [caseStudies]);

  const industries = uniqBy(
    caseStudies.map((cs) => cs.industry),
    "slug",
  );

  const filteredCaseStudyTabs: IndustryTabProps[] = useMemo(
    () =>
      industries.map((i) => {
        const studiesForIndustry = caseStudies.filter((cs) => cs.industry.slug === i.slug);
        const tabOrder = industryTabOrder.indexOf(i.slug);

        return {
          // Use the tab order defined above for any tabs we want in a specific order, and put all other industries at the end
          tabOrder: tabOrder > -1 ? tabOrder : industryTabOrder.length,
          label: i.name,
          items: <CaseStudyGrid caseStudies={studiesForIndustry} />,
        };
      }),
    [caseStudies, industries],
  );

  return (
    <FilterTabs
      tabs={[
        {
          label: "All stories",
          items: allCaseStudies,
        },
        ...sortBy(filteredCaseStudyTabs, "tabOrder"),
      ]}
    />
  );
}

function CaseStudyGrid({ caseStudies }: { caseStudies: CaseStudySimple[] }): JSX.Element {
  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} columnGap="small" justifyItems="center">
      {caseStudies.map((caseStudy) => (
        <CaseStudyCard key={caseStudy.customer.slug} {...caseStudy} />
      ))}
    </SimpleGrid>
  );
}

function CaseStudyCard(caseStudy: CaseStudySimple): JSX.Element {
  return (
    <Card
      variant="shadow"
      href={`/customers/${caseStudy.customer.slug}`}
      media={
        <Box pos="relative" boxSize="100%">
          <DecorativeBackgroundImage src={caseStudy.croppedImageUrl ?? caseStudy.fullImageUrl} />
          <DecorativeBackgroundImage src={cardOverlay} />
          <AspectRatio position="absolute" bottom="big" left="big" ratio={160 / 42} w="160px" maxW="50%" maxH="42px">
            <Image
              width="160"
              height="42"
              src={caseStudy.customer.logoMonoUrl ?? caseStudy.customer.logoUrl}
              alt={`${caseStudy.customer.name} logo.`}
              style={{
                objectFit: "contain",
                objectPosition: "left bottom",
                filter: "brightness(0) invert(1)",
              }}
            />
          </AspectRatio>
        </Box>
      }
      chip={{ text: caseStudy.industry.name, variant: getCaseStudyIndustryColor(caseStudy.industry.slug) }}
      title={caseStudy.title}
      mb="bigger"
    />
  );
}
